import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import React, { forwardRef } from 'react'
import { Link } from './link'

/**
 * Catalyst's Button on the headless tokens (the design system's
 * code/console/MIGRATION.md, button.tsx table). Five solid colours: `steel` (the primary action),
 * `neutral` (the default), and the status colours `danger`, `good`, `warn`.
 * Catalyst's other 23 keys still work: each is an alias that folds onto
 * one of the five (`ALIASES`), so `color="red"` and `color="danger"` are
 * the same button.
 */
const styles = {
  base: [
    // Base
    'relative isolate inline-flex items-baseline justify-center gap-x-2 rounded-lg border text-base/6 font-semibold',
    // Sizing
    'px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)] sm:text-sm/6',
    // Focus
    'focus:not-data-focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-focus',
    // Disabled
    'data-disabled:opacity-50',
    // Icon
    '*:data-[slot=icon]:-mx-0.5 *:data-[slot=icon]:my-0.5 *:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:self-center *:data-[slot=icon]:text-(--btn-icon) sm:*:data-[slot=icon]:my-1 sm:*:data-[slot=icon]:size-4 forced-colors:[--btn-icon:ButtonText] forced-colors:data-hover:[--btn-icon:ButtonText]',
  ],
  solid: [
    // Optical border, implemented as the button background to avoid corner artifacts
    'border-transparent bg-(--btn-border)',
    // Button background, implemented as foreground layer to stack on top of pseudo-border layer
    'before:absolute before:inset-0 before:-z-10 before:rounded-[calc(var(--radius-lg)-1px)] before:bg-(--btn-bg)',
    // Drop shadow, applied to the inset `before` layer so it blends with the border
    'before:shadow-control',
    // Shim/overlay, inset to match button foreground and used for hover state + highlight shadow
    'after:absolute after:inset-0 after:-z-10 after:rounded-[calc(var(--radius-lg)-1px)]',
    // Inner highlight shadow
    'after:shadow-[inset_0_1px_--theme(--color-white/15%)]',
    // Overlay on hover, a deeper one on press
    'data-hover:after:bg-(--btn-hover-overlay) data-active:after:bg-(--btn-press-overlay)',
    // Disabled
    'data-disabled:before:shadow-none data-disabled:after:shadow-none',
  ],
  outline: [
    // Base
    'border-line-strong text-fg data-hover:border-line-hover data-hover:bg-wash data-active:bg-wash-2',
    // Icon
    '[--btn-icon:var(--color-fg-3)] data-hover:[--btn-icon:var(--color-fg-2)] data-active:[--btn-icon:var(--color-fg-2)]',
  ],
  plain: [
    // Base
    'border-transparent text-fg data-hover:bg-wash-2 data-active:bg-wash-3',
    // Icon
    '[--btn-icon:var(--color-fg-3)] data-hover:[--btn-icon:var(--color-fg-2)] data-active:[--btn-icon:var(--color-fg-2)]',
  ],
  colors: {
    steel: [
      'text-on-accent [--btn-bg:var(--color-accent)] [--btn-border:var(--color-accent-edge)] [--btn-hover-overlay:var(--color-accent-hover)] [--btn-press-overlay:color-mix(in_oklab,var(--color-accent)_86%,var(--color-accent-deep))]',
      '[--btn-icon:var(--color-on-accent)]/70',
    ],
    neutral: [
      'text-solid-fg [--btn-bg:var(--color-solid)] [--btn-border:var(--color-solid-edge)] [--btn-hover-overlay:var(--color-solid-overlay)] [--btn-press-overlay:var(--color-wash-3)]',
      '[--btn-icon:var(--color-fg-3)]',
    ],
    danger: [
      'text-danger-deep [--btn-bg:var(--color-danger)] [--btn-border:var(--color-danger)]/80 [--btn-hover-overlay:var(--color-white)]/20 [--btn-press-overlay:var(--color-black)]/10',
      '[--btn-icon:var(--color-danger-deep)]/70',
    ],
    good: [
      'text-good-deep [--btn-bg:var(--color-good)] [--btn-border:var(--color-good)]/80 [--btn-hover-overlay:var(--color-white)]/20 [--btn-press-overlay:var(--color-black)]/10',
      '[--btn-icon:var(--color-good-deep)]/70',
    ],
    warn: [
      'text-warn-deep [--btn-bg:var(--color-warn)] [--btn-border:var(--color-warn)]/80 [--btn-hover-overlay:var(--color-white)]/20 [--btn-press-overlay:var(--color-black)]/10',
      '[--btn-icon:var(--color-warn-deep)]/70',
    ],
  },
}

export type ButtonTone = keyof typeof styles.colors

/** Catalyst's colour keys, each folded onto one of the five tones. */
export const BUTTON_ALIASES = {
  'dark/zinc': 'neutral',
  light: 'neutral',
  'dark/white': 'neutral',
  dark: 'neutral',
  white: 'neutral',
  zinc: 'neutral',
  indigo: 'steel',
  cyan: 'steel',
  blue: 'steel',
  sky: 'steel',
  violet: 'steel',
  purple: 'steel',
  red: 'danger',
  rose: 'danger',
  pink: 'danger',
  fuchsia: 'danger',
  orange: 'danger',
  amber: 'warn',
  yellow: 'warn',
  lime: 'good',
  green: 'good',
  emerald: 'good',
  teal: 'good',
} as const satisfies Record<string, ButtonTone>

export type ButtonColor = ButtonTone | keyof typeof BUTTON_ALIASES

function toneOf(color: ButtonColor): ButtonTone {
  return color in BUTTON_ALIASES ? BUTTON_ALIASES[color as keyof typeof BUTTON_ALIASES] : (color as ButtonTone)
}

type ButtonProps = (
  | { color?: ButtonColor; outline?: never; plain?: never }
  | { color?: never; outline: true; plain?: never }
  | { color?: never; outline?: never; plain: true }
) & { className?: string; children: React.ReactNode } & (
    | ({ href?: never } & Omit<Headless.ButtonProps, 'as' | 'className'>)
    | ({ href: string } & Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>)
  )

export const Button = forwardRef(function Button(
  { color, outline, plain, className, children, ...props }: ButtonProps,
  ref: React.ForwardedRef<HTMLElement>
) {
  let classes = clsx(
    className,
    styles.base,
    outline ? styles.outline : plain ? styles.plain : clsx(styles.solid, styles.colors[toneOf(color ?? 'neutral')])
  )

  return typeof props.href === 'string' ? (
    <Link {...props} className={classes} ref={ref as React.ForwardedRef<HTMLAnchorElement>}>
      <TouchTarget>{children}</TouchTarget>
    </Link>
  ) : (
    <Headless.Button {...props} className={clsx(classes, 'cursor-default')} ref={ref}>
      <TouchTarget>{children}</TouchTarget>
    </Headless.Button>
  )
})

/**
 * Expand the hit area to at least 44×44px on touch devices
 */
export function TouchTarget({ children }: { children: React.ReactNode }) {
  return (
    <>
      <span
        className="absolute top-1/2 left-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 pointer-fine:hidden"
        aria-hidden="true"
      />
      {children}
    </>
  )
}
